import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { clsx } from '../../ds/clsx';
import { useEventTypeOptions, type EventTypeOptionGroup, type EventTypeRef } from './api';
import styles from './EventTypeCombobox.module.css';

/**
 * Ereignistyp-Suchfeld (Terminierung A2, 05–07 settings and 08b): an input with the drawn magnifier, the
 * listbox in the flow below it with group headers („Aus seinen Listen" / „Zuletzt von Ihnen gebucht" without
 * text for a contact, else „Eigene" / „Organisation"), the typed text highlighted in the names and
 * „+ Neues Ereignis „<Text>“ anlegen" for users who may create event types (opens the editor in a new tab).
 */

export interface EventTypeComboboxProps {
  /** The contact of the call (08b groups); null in the list settings. */
  contactId: string | null;
  value: EventTypeRef | null;
  onChange: (value: EventTypeRef) => void;
  /** Accessible name of the field. */
  label: string;
  placeholder?: string | undefined;
  open?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  invalid?: boolean | undefined;
  autoFocus?: boolean | undefined;
  describedBy?: string | undefined;
}

interface Option {
  id: string;
  name: string;
  durationMinutes: number;
  meta: string;
  group: string;
  showDuration: boolean;
}

function flatten(groups: EventTypeOptionGroup[]): Option[] {
  return groups.flatMap((group) =>
    group.items.map((item) => ({
      id: item.eventTypeId,
      name: item.name,
      durationMinutes: item.durationMinutes,
      meta: item.meta,
      group: group.label,
      // The contact groups name the duration next to the type (08b); the search groups carry it in the meta.
      showDuration: group.key === 'lists' || group.key === 'recent',
    })),
  );
}

function Highlighted({ text, query }: { text: string; query: string }) {
  const needle = query.trim();
  if (!needle) return <>{text}</>;
  const index = text.toLowerCase().indexOf(needle.toLowerCase());
  if (index < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, index)}
      <mark className={styles.mark}>{text.slice(index, index + needle.length)}</mark>
      {text.slice(index + needle.length)}
    </>
  );
}

export function EventTypeCombobox({
  contactId,
  value,
  onChange,
  label,
  placeholder = 'Ereignis suchen',
  open: controlledOpen,
  onOpenChange,
  invalid = false,
  autoFocus = false,
  describedBy,
}: EventTypeComboboxProps) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [innerOpen, setInnerOpen] = useState(false);
  const open = controlledOpen ?? innerOpen;
  const [text, setText] = useState('');
  const [active, setActive] = useState(-1);
  const options = useEventTypeOptions(contactId, text, open);
  const flat = useMemo(() => flatten(options.data?.groups ?? []), [options.data]);
  const canCreate = (options.data?.canCreate ?? false) && text.trim().length > 0;
  const count = flat.length + (canCreate ? 1 : 0);

  function setOpen(next: boolean) {
    if (controlledOpen === undefined) setInnerOpen(next);
    onOpenChange?.(next);
  }

  useEffect(() => {
    setActive(-1);
  }, [text, options.data]);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  function choose(option: Option) {
    onChange({ id: option.id, name: option.name, durationMinutes: option.durationMinutes });
    setText('');
    setOpen(false);
  }

  function createNew() {
    window.open('/calendar/event-types/new', '_blank', 'noopener');
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) setOpen(true);
      else setActive((index) => (count === 0 ? -1 : (index + 1) % count));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => (count === 0 ? -1 : index <= 0 ? count - 1 : index - 1));
    } else if (event.key === 'Enter' && open) {
      event.preventDefault();
      const option = flat[active];
      if (option) choose(option);
      else if (canCreate && active === flat.length) createNew();
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
    }
  }

  const listId = `${id}-list`;
  const activeId = open && active >= 0 && active < count ? `${id}-option-${active}` : undefined;
  let lastGroup = '';

  return (
    <div
      className={styles.wrap}
      onBlur={(event) => {
        if (open && !event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <div className={clsx(styles.field, open && styles.fieldOpen, invalid && styles.fieldInvalid)}>
        <span className={styles.magnifier} aria-hidden="true">
          <span className={styles.lens} />
          <span className={styles.handle} />
        </span>
        <input
          ref={inputRef}
          className={styles.input}
          role="combobox"
          aria-label={label}
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeId}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          placeholder={placeholder}
          value={open ? text : value ? `${value.name} · ${value.durationMinutes} Min.` : ''}
          onChange={(event) => {
            setText(event.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
      </div>
      {open ? (
        <div className={styles.popup} id={listId} role="listbox" aria-label={label}>
          {options.isPending ? <div className={styles.status}>Lädt …</div> : null}
          {!options.isPending && flat.length === 0 && !canCreate ? (
            <div className={styles.status}>Kein Ereignis gefunden.</div>
          ) : null}
          {flat.map((option, index) => {
            const header = option.group !== lastGroup ? option.group : null;
            lastGroup = option.group;
            return (
              <div key={`${option.group}-${option.id}`} role="presentation">
                {header ? (
                  <div className={styles.group} role="presentation">
                    {header}
                  </div>
                ) : null}
                <div
                  id={`${id}-option-${index}`}
                  role="option"
                  aria-selected={value?.id === option.id}
                  className={clsx(styles.option, index === active && styles.optionActive)}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => choose(option)}
                >
                  <div className={styles.optionName}>
                    <Highlighted text={option.name} query={text} />
                    {option.showDuration ? ` · ${option.durationMinutes} Min.` : ''}
                  </div>
                  <div className={styles.optionMeta}>{option.meta}</div>
                </div>
              </div>
            );
          })}
          {canCreate ? (
            <>
              <div className={styles.separator} role="presentation" />
              <div
                id={`${id}-option-${flat.length}`}
                role="option"
                aria-selected={false}
                className={clsx(styles.option, styles.create, active === flat.length && styles.optionActive)}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setActive(flat.length)}
                onClick={createNew}
              >
                + Neues Ereignis „{text.trim()}“ anlegen
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
